import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';

import { URL } from '@/config/url';
import Watermark from '@/shared/assets/article-image-overlay.svg?svg';
import { EntityListArticleItem } from '@/shared/api/__generated__/models';
import * as BaseArticle from '@/shared/ui/Article';
import { formatArticleDate } from '@/shared/utils';
import styles from './Article.module.css';

interface ArticleProps {
  article: EntityListArticleItem;
  type?: 'hero' | 'tile' | 'card';
  className?: string;
}

const titlePropertiesByType = {
  hero: {
    fontSize: 24,
    fontWeight: 800,
    lineHeight: 28,
    letterSpacing: -0.264,
  },
  tile: {
    fontWeight: 800,
    fontSize: 18,
    lineHeight: 24,
    letterSpacing: -0.198,
  },
  card: {
    fontWeight: 800,
    fontSize: 18,
    lineHeight: 24,
    letterSpacing: -0.198,
  },
};

export const Article = ({
  article,
  type = 'tile',
  className,
}: ArticleProps) => {
  const visibleRubric = article.rubrics?.[0].name;

  return (
    <Link
      className={clsx(styles.wrapper, className)}
      data-type={type}
      href={URL.article(article.slug ?? '')}
    >
      <span className={styles.header}>
        {formatArticleDate(article.publish_date ?? '')}{' '}
        {visibleRubric ? '·' : ''} {visibleRubric}
      </span>
      <div
        className={styles.imageWrapper}
        style={
          {
            '--background-image': `url(${article.cover_url ?? ''})`,
          } as React.CSSProperties
        }
      >
        <Image
          className={styles.image}
          src={article.cover_url ?? ''}
          alt={article.title ?? ''}
          fill
          loading="lazy"
        />
        <Watermark className={styles.imageOverlay} />
      </div>
      <div className={styles.textBlock}>
        <BaseArticle.ArticleTitle
          as="p"
          fontFamily="var(--font-roboto-flex)"
          {...titlePropertiesByType[type]}
          className={styles.title}
        >
          {article.title}
        </BaseArticle.ArticleTitle>
        <BaseArticle.ArticleDescription
          as="p"
          fontSize={14}
          lineHeight="160%"
          fontWeight={400}
          fontFamily="var(--font-merriweather-mono)"
          className={styles.description}
        >
          {article.short_description}
        </BaseArticle.ArticleDescription>
      </div>
    </Link>
  );
};
